import { useCallback, useEffect, useState } from 'react'
import { getUnreadMessages } from '../../api/getUnreadMessages'
import type { NormalizedUnreadMessage } from '../../types/unreadMessages'
import { filterByVariant, mapUnreadMessagesResponse } from '../../utils/mapUnreadMessagesResponse'
import { UnreadMessageCard } from './UnreadMessageCard'

export type MedicalLinkCredentialsLite = {
  piBearerToken: string
  loggedUserId: string
}

export type UnreadMessagesListProps = {
  variant: 'unread' | 'urgent'
  /** When ``fr``, all list chrome + read-aloud prompt use French (not browser locale alone). */
  language?: 'en' | 'fr'
  getCredentials: () => MedicalLinkCredentialsLite
  /** Chat message id that hosts this list (for caching rows in the widget). */
  parentMessageId: string
  onRowsLoaded?: (parentMessageId: string, rows: NormalizedUnreadMessage[]) => void
  onMessageOpen?: (parentMessageId: string, row: NormalizedUnreadMessage, index: number) => void
  /** Hide Yes/No read-aloud card after play or decline. */
  hideReadAloudPrompt?: boolean
  onReadAloudRequest?: (parentMessageId: string, rows: NormalizedUnreadMessage[]) => void
  onReadAloudDecline?: (parentMessageId: string) => void
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; rows: NormalizedUnreadMessage[] }

export function UnreadMessagesList(props: UnreadMessagesListProps) {
  const {
    variant,
    language = 'en',
    getCredentials,
    parentMessageId,
    onRowsLoaded,
    onMessageOpen,
    hideReadAloudPrompt,
    onReadAloudRequest,
    onReadAloudDecline,
  } = props
  const [state, setState] = useState<LoadState>({ status: 'idle' })

  const runLoad = useCallback(async () => {
    setState({ status: 'loading' })
    try {
      const c = getCredentials()
      const urgent: 0 | 1 = variant === 'urgent' ? 1 : 0
      const raw = await getUnreadMessages(
        {
          bearerToken: c.piBearerToken,
          csrfToken: '',
        },
        urgent,
      )
      const mapped = mapUnreadMessagesResponse(raw)
      const rows = filterByVariant(mapped, variant, urgent)
      setState({ status: 'success', rows })
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : language === 'fr'
            ? 'Impossible de charger les messages.'
            : 'Failed to load messages.'
      setState({ status: 'error', message: msg })
    }
  }, [getCredentials, variant, language])

  useEffect(() => {
    void runLoad()
  }, [runLoad])

  const loadedRows = state.status === 'success' ? state.rows : null
  useEffect(() => {
    if (!loadedRows) return
    onRowsLoaded?.(parentMessageId, loadedRows)
  }, [loadedRows, parentMessageId, onRowsLoaded])

  const fr = language === 'fr'

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="vnw-unreadList vnw-unreadList--loading" role="status" aria-live="polite">
        <div className="vnw-unreadListSpinner" aria-hidden="true" />
        <p className="vnw-unreadListLoadingText">
          {fr ? 'Chargement des messages…' : 'Loading messages…'}
        </p>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="vnw-unreadList vnw-unreadList--error" role="alert">
        <p className="vnw-unreadListErrorText">{state.message}</p>
        <button type="button" className="vnw-unreadListRetry" onClick={() => void runLoad()}>
          {fr ? 'Réessayer' : 'Retry'}
        </button>
      </div>
    )
  }

  if (state.rows.length === 0) {
    return (
      <div className="vnw-unreadList vnw-unreadList--empty" role="status">
        <p className="vnw-unreadListEmptyTitle">
          {variant === 'urgent'
            ? fr
              ? 'Aucun message urgent'
              : 'No urgent messages'
            : fr
              ? 'Aucun message non lu'
              : 'No unread messages'}
        </p>
        <p className="vnw-unreadListEmptySub">
          {fr ? 'Vous êtes à jour.' : 'You are all caught up.'}
        </p>
      </div>
    )
  }
  const showReadAloudOffer =
    Boolean(onReadAloudRequest && onReadAloudDecline) &&
    !hideReadAloudPrompt &&
    state.rows.length > 0

  return (
    <div className="vnw-unreadListWrap">
      <div className="vnw-unreadList" role="list" aria-label="Unread messages">
        {state.rows.map((m, index) => (
          <div key={m.id} className="vnw-unreadListItem" role="listitem">
            <UnreadMessageCard
              message={m}
              index={index}
              onOpen={() => onMessageOpen?.(parentMessageId, m, index)}
            />
          </div>
        ))}
      </div>
      {showReadAloudOffer ? (
        <div className="vnw-unreadReadAloudPrompt" role="group" aria-label={fr ? 'Lecture à voix haute' : 'Read aloud'}>
          <p className="vnw-unreadReadAloudPromptText">
            {fr
              ? 'Souhaitez-vous que je lise ces messages à voix haute (AWS Polly) ?'
              : 'Would you like me to read these messages aloud (AWS Polly)?'}
          </p>
          <div className="vnw-unreadReadAloudPromptBtns">
            <button
              type="button"
              className="vnw-unreadReadAloudBtn vnw-unreadReadAloudBtn--yes"
              onClick={() => onReadAloudRequest!(parentMessageId, state.rows)}
            >
              {fr ? 'Lire à voix haute' : 'Read aloud'}
            </button>
            <button
              type="button"
              className="vnw-unreadReadAloudBtn vnw-unreadReadAloudBtn--no"
              onClick={() => onReadAloudDecline!(parentMessageId)}
            >
              {fr ? 'Pas maintenant' : 'Not now'}
            </button>
          </div>
          <p className="vnw-unreadReadAloudHint">
            {fr
              ? 'Pour afficher la liste : « Afficher mes messages non lus ». Pour écouter ici : « Lire à voix haute », « Lis-moi ça » ou « Lire mes messages ».'
              : 'To load this list, say: “Show me my unread messages.” To hear it here, say: “Read it for me,” “Read my messages,” or “Read out my messages.”'}
          </p>
        </div>
      ) : null}
    </div>
  )
}
